import { useParams, useNavigate } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { apiClient } from '@/services/apiClient'

interface TradeDetail {
  trade: {
    id: string
    user_id: string
    symbol: string
    order_type: string
    position_type: string
    quantity: number
    open_price: number
    close_price: number | null
    margin_required: number
    fee: number
    status: string
    pnl: number
    leverage: number
    open_time: string
    close_time: string | null
    created_at: string
    updated_at: string
  }
  user: {
    id: string
    full_name: string
    email: string
    phone?: string
    operator_id?: string
    operator_name?: string
  }
  market_data?: {
    current_price: number
    high_price: number
    low_price: number
    change_percent: number
  }
}

export default function TradeDetailPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [trade, setTrade] = useState<TradeDetail | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (id) {
      fetchTradeDetail()
    }
  }, [id])

  const fetchTradeDetail = async () => {
    try {
      setLoading(true)
      const response = await apiClient.getTradeDetail(id!)
      if (response.success) {
        setTrade(response.data)
      } else {
        console.error('Failed to fetch trade detail:', response.message)
      }
    } catch (error) {
      console.error('Error fetching trade detail:', error)
    } finally {
      setLoading(false)
    }
  }

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { label: string; className: string }> = {
      open: { label: '持仓中', className: 'bg-blue-100 text-blue-800' },
      closed: { label: '已平仓', className: 'bg-green-100 text-green-800' },
      cancelled: { label: '已取消', className: 'bg-gray-100 text-gray-800' },
      liquidated: { label: '强制平仓', className: 'bg-red-100 text-red-800' }
    }
    const config = statusConfig[status] || { label: status, className: 'bg-gray-100 text-gray-800' }
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.className}`}>
        {config.label}
      </span>
    )
  }

  const getOrderTypeBadge = (type: string) => {
    const typeConfig: Record<string, { label: string; className: string }> = {
      buy: { label: '买入', className: 'bg-green-50 text-green-700' },
      sell: { label: '卖出', className: 'bg-red-50 text-red-700' }
    }
    const config = typeConfig[type] || { label: type, className: 'bg-gray-50 text-gray-700' }
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-semibold ${config.className}`}>
        {config.label}
      </span>
    )
  }

  const getPositionTypeBadge = (type: string) => {
    const typeConfig: Record<string, { label: string; className: string }> = {
      long: { label: '做多', className: 'bg-blue-50 text-blue-700' },
      short: { label: '做空', className: 'bg-orange-50 text-orange-700' }
    }
    const config = typeConfig[type] || { label: type, className: 'bg-gray-50 text-gray-700' }
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-semibold ${config.className}`}>
        {config.label}
      </span>
    )
  }

  const getPnlColor = (pnl: number) => {
    if (pnl > 0) return 'text-green-600'
    if (pnl < 0) return 'text-red-600'
    return 'text-gray-600'
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('zh-CN', {
      style: 'currency',
      currency: 'CNY',
    }).format(value)
  }

  if (loading) {
    return (
      <div className="page-content">
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">加载中...</div>
        </div>
      </div>
    )
  }

  if (!trade) {
    return (
      <div className="page-content">
        <div className="text-center py-12">
          <div className="text-gray-500 mb-4">交易订单不存在</div>
          <button
            onClick={() => navigate('/trades')}
            className="btn btn-primary"
          >
            返回交易列表
          </button>
        </div>
      </div>
    )
  }

  const isOpen = trade.trade.status === 'open'

  return (
    <div className="page-content">
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="page-title">交易详情</h1>
            <p className="page-subtitle">订单ID: {trade.trade.id}</p>
          </div>
          <div className="flex items-center space-x-4">
            {getStatusBadge(trade.trade.status)}
            <button
              onClick={() => navigate('/trades')}
              className="btn btn-secondary"
            >
              返回列表
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* 交易信息 */}
        <div className="lg:col-span-2 space-y-6">
          <div className="card">
            <div className="card-header flex items-center justify-between">
              <h3 className="card-title">交易信息</h3>
              {getStatusBadge(trade.trade.status)}
            </div>
            <div className="card-body">
              <div className="grid grid-cols-2 gap-6">
                {/* 基本信息 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">交易对</label>
                  <div className="text-lg font-semibold text-gray-900">{trade.trade.symbol}</div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">交易类型</label>
                  <div className="flex gap-2">
                    {getOrderTypeBadge(trade.trade.order_type)}
                    {getPositionTypeBadge(trade.trade.position_type)}
                  </div>
                </div>

                {/* 价格信息 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">开仓价</label>
                  <div className="text-lg font-semibold text-gray-900">{trade.trade.open_price.toFixed(4)}</div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">平仓价</label>
                  <div className="text-lg font-semibold text-gray-900">
                    {trade.trade.close_price ? trade.trade.close_price.toFixed(4) : '-'}
                  </div>
                </div>

                {/* 数量和杠杆 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">交易数量</label>
                  <div className="text-lg font-semibold text-gray-900">{trade.trade.quantity}</div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">杠杆倍数</label>
                  <div className="text-lg font-semibold text-gray-900">{trade.trade.leverage.toFixed(2)}x</div>
                </div>

                {/* 保证金和手续费 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">保证金</label>
                  <div className="text-lg font-semibold text-gray-900">{formatCurrency(trade.trade.margin_required)}</div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">手续费</label>
                  <div className="text-lg font-semibold text-red-600">{formatCurrency(trade.trade.fee)}</div>
                </div>

                {/* 盈亏 */}
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">盈亏</label>
                  <div className={`text-2xl font-bold ${getPnlColor(trade.trade.pnl)}`}>
                    {formatCurrency(trade.trade.pnl)}
                  </div>
                </div>
              </div>

              {/* 时间信息 */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">开仓时间</label>
                    <div className="text-sm text-gray-900">{new Date(trade.trade.open_time).toLocaleString()}</div>
                  </div>
                  {trade.trade.close_time && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">平仓时间</label>
                      <div className="text-sm text-gray-900">{new Date(trade.trade.close_time).toLocaleString()}</div>
                    </div>
                  )}
                </div>
              </div>

              {/* 市场数据 */}
              {trade.market_data && isOpen && (
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <h4 className="font-medium text-gray-900 mb-3">当前市场数据</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">当前价格</label>
                      <div className="text-lg font-semibold text-gray-900">{trade.market_data.current_price.toFixed(4)}</div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">涨跌幅</label>
                      <div className={`text-lg font-semibold ${trade.market_data.change_percent >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {trade.market_data.change_percent >= 0 ? '+' : ''}{trade.market_data.change_percent.toFixed(2)}%
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">最高价</label>
                      <div className="text-sm text-gray-900">{trade.market_data.high_price.toFixed(4)}</div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">最低价</label>
                      <div className="text-sm text-gray-900">{trade.market_data.low_price.toFixed(4)}</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* 用户信息 */}
        <div className="space-y-6">
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">用户信息</h3>
            </div>
            <div className="card-body">
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700">姓名</label>
                  <div className="text-sm text-gray-900">{trade.user.full_name}</div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">邮箱</label>
                  <div className="text-sm text-gray-900">{trade.user.email}</div>
                </div>
                {trade.user.phone && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">手机号</label>
                    <div className="text-sm text-gray-900">{trade.user.phone}</div>
                  </div>
                )}
                {trade.user.operator_id && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">运营商</label>
                    <div className="text-sm text-gray-900">
                      {trade.user.operator_name} ({trade.user.operator_id})
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* 交易统计 */}
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">交易统计</h3>
            </div>
            <div className="card-body">
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">持仓时长</span>
                  <span className="text-sm font-medium text-gray-900">
                    {trade.trade.close_time
                      ? Math.floor((new Date(trade.trade.close_time).getTime() - new Date(trade.trade.open_time).getTime()) / (1000 * 60 * 60)) + ' 小时'
                      : Math.floor((new Date().getTime() - new Date(trade.trade.open_time).getTime()) / (1000 * 60 * 60)) + ' 小时'}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">盈亏率</span>
                  <span className={`text-sm font-medium ${getPnlColor(trade.trade.pnl)}`}>
                    {trade.trade.margin_required > 0
                      ? ((trade.trade.pnl / trade.trade.margin_required) * 100).toFixed(2) + '%'
                      : '-'}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">状态</span>
                  <span className="text-sm font-medium text-gray-900">{getStatusBadge(trade.trade.status)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
